"use client";

import React from "react";
import { Tag, Folder, Loader, ArrowRight, ArrowLeft, Mail } from "lucide-react";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";

declare global {
  interface window {
    wvcClient: any;
  }
}

function ProductInquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
  } = useWvcForm();

  if (isSubmitted && !submitError) {
    return (
      <div className="relative z-10 rounded-md border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-primary/15">
          <Mail className="size-5 text-primary" />
        </div>
        <h3 className="mb-2 text-xl font-bold tracking-tight text-foreground">
          Request Sent
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">{successMessage}</p>
    
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit} className="relative z-10 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <FormField
          control={control}
          name="first_name"
          render={({ field }) => (
            <FormItem className="md:col-span-5">
              <FormLabel className="sr-only">Full Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Full Name"
                  className="h-11 rounded-md border-border bg-card px-4 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="email"
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email",
            },
          }}
          render={({ field }) => (
            <FormItem className="md:col-span-7">
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Email Address"
                  className="h-11 rounded-md border-border bg-card px-4 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="phone"
          render={({ field }) => (
            <FormItem className="md:col-span-12">
              <FormLabel className="sr-only">Phone Number</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder="+27 (12) 345-6789"
                  className="h-11 rounded-md border-border bg-card px-4 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="product_name"
          rules={{ required: "Product Name is required" }}
          render={({ field }) => (
            <FormItem className="md:col-span-12">
              <FormLabel className="sr-only">Product Name</FormLabel>
              <FormControl>
                <Input
                  readOnly
                  disabled
                  className="h-11 rounded-md border-border bg-muted/50 px-4 text-sm text-foreground"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
       
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="group h-11 w-full rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        {isSubmitting ? (
          <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Submit Part Inquiry
            <ArrowRight className="ml-1 size-4 transition-transform duration-200 group-hover:translate-x-1" />
          </>
        )}
      </Button>

      {submitError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <p className="text-xs text-destructive">{submitError}</p>
        </div>
      )}
    </Form>
  );
}

function SingleProductContent() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-muted/30 animate-pulse rounded-md" />
        <div className="space-y-6">
          <div className="h-12 w-3/4 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-8 w-1/3 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-24 w-full bg-muted/30 animate-pulse rounded-md" />
          <div className="h-12 w-full bg-muted/30 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground font-default">Unable to load industrial part details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground font-default">Industrial component not found.</p>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="space-y-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="relative">
          <div className="relative overflow-hidden border border-border bg-card rounded-md">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover aspect-square"
                loading="lazy"
              />
            ) : (
              <div className="aspect-square bg-muted flex items-center justify-center">
                <span className="text-muted-foreground font-mono text-xs uppercase tracking-widest">
                  No Image Available
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          {product.categories && product.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {product.categories.map((cat, i) => (
                <span
                  key={cat.id}
                  data-index={i}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-primary"
                >
                  {cat.name}
                  {i < product.categories.length - 1 && (
                    <span className="ml-2 text-muted-foreground">/</span>
                  )}
                </span>
              ))}
            </div>
          )}

          <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight mb-6 text-foreground">
            {product.title}
          </h1>

          <Separator className="bg-border mb-8" />

          {product.short_description && (
            <div
              className="font-default text-lg leading-relaxed text-foreground/90 mb-8 [&_p]:mb-3 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="my-8" />
          <h3 className="font-default text-2xl font-bold mb-6 text-foreground">
            Send Inquiry
          </h3>
          <FormProvider
            formId="product_inquiry_form"
            sectionName="single-product-template-section"
            defaultValues={{
              first_name: "",
              email: "",
              phone: "",
              product_name: product.title,
              product_sku: product.sku || "",
            }}
            validationRules={{
              first_name: { required: false },
              email: { required: true, pattern: "email" },
              phone: { required: false },
              product_name: { required: true },
              product_sku: { required: true },
            }}
            submitText="Submit Inquiry"
            successMessage="Your inquiry has been received. Our team will review your request and contact you shortly."
            errorMessage="There was an error sending your request. Please try again."
          >
            <ProductInquiryFormContent />
          </FormProvider>

          <Separator className="bg-border my-6" />

          <div className="space-y-4">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3">
                <Folder className="size-4 text-primary mt-1 shrink-0" />
                <div>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mr-2">
                    System Type:
                  </span>
                  {product.categories.map((cat, i) => (
                    <Link
                      key={cat.id}
                      data-index={i}
                      to={cat.link || "/PartsCataloguePage"}
                      className="font-default text-sm text-foreground hover:text-primary transition-colors"
                    >
                      {cat.name}
                      {i < product.categories.length - 1 && (
                        <span className="text-muted-foreground">, </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3 flex-wrap">
                <Tag className="size-4 text-primary mt-1 shrink-0" />
                <div className="flex flex-wrap gap-2">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mr-1 mt-1">
                    Specifications:
                  </span>
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      data-index={i}
                      to={tag.link || "/PartsCataloguePage"}
                      className="font-mono text-xs uppercase tracking-widest px-3 py-1 border border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors rounded-md"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {product.long_description && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-8 border-t border-border">
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-3">
              [01]
            </div>
            <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Technical Overview
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div
              className="font-default text-base md:text-lg leading-relaxed text-foreground/90 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-default [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:font-default [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-5 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}

      {product.customFields && Object.keys(product.customFields).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-8 border-t border-border">
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-3">
              [02]
            </div>
            <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Industrial Specs
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Card className="bg-card/50 backdrop-blur-sm border border-border p-0 shadow-md rounded-md">
              <dl className="divide-y divide-border">
                {Object.entries(product.customFields).map(([key, value], i) => {
                  if (
                    value === null ||
                    value === undefined ||
                    value === "" ||
                    typeof value === "object"
                  )
                    return null;
                  return (
                    <div
                      key={key}
                      data-index={i}
                      className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-6 py-4"
                    >
                      <dt className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {key.replace(/_/g, " ")}
                      </dt>
                      <dd className="sm:col-span-2 font-default text-foreground">
                        {String(value)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="2772"
      id="single-product-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

      <div className="max-w-7xl mx-auto relative">
        <div className="mb-10 flex items-center justify-between border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">
              IMCNC Designs / CATALOGUE
            </span>
          </div>
          <Link
            to="/PartsCataloguePage"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to Parts Catalogue
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductContent />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}